<script lang="ts">
  import Tiptap from "$app/components/Tiptap.svelte"
  import { ChevronDown } from "lucide-svelte"
  import { slide } from "svelte/transition"
  import { xhr } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { toast } from "$app/utils"
  import {
    deleteFileFromList,
    uploadFile
  } from "$app/Pages/Plans/Community/utils"
  import { appStore } from "$app/store.svelte"
  import { wait } from "$app/utils"

  interface Props {
    channel: ForumChannelType
    channels: ForumChannelType[]
  }

  let { channel, channels }: Props = $props()

  let newPost: ForumPostType | null = null
  let newPostContent = $state("")
  let savingPost = $state(false)
  let newPostUserId = appStore.current_user.id
  let schedulePost = $state(false)
  let currentTime = new Date()
  let schedulePostValue = currentTime.toISOString().slice(0, 16)
  let uploadingFile = $state(false)
  let files: ActiveStorageType[] = $state([])

  async function createPost(content: string): Promise<void> {
    if (
      schedulePost &&
      schedulePostValue <= new Date().toISOString().slice(0, 16)
    ) {
      alert("Please select a future date and time for scheduled posts")
      return
    }

    savingPost = true

    let forum_post_data = {
      forum_channel_id: form.channel_id,
      user_id: newPostUserId,
      content: content
    }

    if (schedulePost) {
      forum_post_data["published_at"] = schedulePostValue
    }

    try {
      const resp = await xhr.post<ForumPostType>(
        `/plans/${appStore.plan.id}/community_posts.json`,
        {
          forum_post: forum_post_data
        }
      )

      appStore.current_channel =
        channels.find((c) => c.id === forum_post_data.forum_channel_id) ||
        appStore.current_channel
      appStore.channelPosts = [resp.data, ...appStore.channelPosts]
      newPost = null
      newPostContent = ""
      files = []
      toast("Post has been created", "success")
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      savingPost = false
    }
  }

  async function addFile(file: File): Promise<void> {
    uploadingFile = true
    try {
      const resp = await uploadFile(file, {
        plan_id: appStore.plan.id,
        channel_id: form.channel_id,
        id: newPost?.id,
        user_id: newPostUserId
      })

      files = resp.files
      newPost = resp
    } catch (e: any) {
      toast("There was an error uploading your file", "error")
    } finally {
      uploadingFile = false
    }
  }

  async function deleteFile(file: ActiveStorageType): Promise<void> {
    if (!newPost) return
    file._selected = true
    files = files
    await wait(50)

    if (await deleteFileFromList(file, appStore.plan.id, newPost.id)) {
      file._selected = true
      files = files.filter((f) => f.id !== file.id)
    }

    file._selected = false
    files = files
  }

  function savePost({ html }: { html: string }) {
    const content = html
    createPost(content)
  }

  let show = $state(false)

  let form = $state({
    channel_id: channel.id,
    user_id: appStore.current_user.id,
    published_at: null
  })
</script>

{#if show}
  {#if appStore.plan.id === 1}
    <div class="grid grid-cols-3 gap-4 items-center pt-2 mb-2">
      <div class="flex items-center justify-end">
        <label for="user_id"> Post As: </label>
      </div>
      <div class="flex justify-start items-center col-span-2">
        <select
          bind:value={form.user_id}
          id="user_id"
          class="max-w-64 rounded-md text-sm border-0 py-1.5 pl-3 pr-10 text-gray-700 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
        >
          {#each appStore.users as user (user.id)}
            <option value={user.id} class="text-sm">
              {user.name}
            </option>
          {/each}
        </select>
      </div>
      <div class="flex items-center justify-end">
        <label for="schedulePost"> Schedule Post: </label>
        <input
          bind:checked={schedulePost}
          id="schedulePost"
          type="checkbox"
          class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 ml-2"
        />
      </div>
      <div class="flex items-center justify-start col-span-2">
        {#if schedulePost}
          <input
            type="datetime-local"
            bind:value={form.published_at}
            class="max-w-72 rounded-md text-sm border-0 py-1.5 pl-3 text-gray-700 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
          <span class="font-bold text-sm ml-2">(UTC)</span>
        {/if}
      </div>
    </div>
  {/if}

  <div class="grid grid-cols-3 gap-4">
    <div class="flex justify-end items-center">
      <label for="channel_id">Channel</label>
    </div>
    <div class="flex justify-start items-center">
      <select
        bind:value={form.channel_id}
        id="channel_id"
        class="max-w-64 rounded-md text-sm border-0 py-1.5 pl-3 pr-10 text-gray-700 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
      >
        {#each channels as chan}
          <option value={chan.id}>{chan.name}</option>
        {/each}
      </select>
    </div>
  </div>

  <div transition:slide class="pt-4 z-0">
    {#key savingPost}
      <Tiptap
        id={`channel-${channel.id}`}
        content={newPostContent}
        saving={savingPost}
        buttons={[
          ["bold", "italic", "underline", "strike"],
          ["bulletList", "orderedList", "blockquote"],
          ["link", "unlink", "image"]
        ]}
        onfileupload={(detail) => addFile(detail)}
        allowFileUploads={true}
        allowEmojis={true}
        showSaveArrow={true}
        allowGiphy={true}
        {files}
        {uploadingFile}
        onsave={savePost}
        onupdate={(data) => {
          newPostContent = data.html
        }}
        ondeletefile={(data) => deleteFile(data)}
      >
        {#snippet bottom()}
          <div class="inline-block">
            <button class="btn btn-sm secondary" onclick={() => (show = false)}>
              Cancel
            </button>
          </div>
        {/snippet}
      </Tiptap>
    {/key}
  </div>
{:else}
  <div class="absolute right-4 top-4">
    <button
      onclick={() => (show = true)}
      data-name="new-channel-button"
      class="text-sm border border-gray-200 bg-green-600 text-white py-1 px-2 rounded hover:shadow flex items-center"
    >
      <ChevronDown class="size-6 text-gray-100" /> New Post
    </button>
  </div>
{/if}
